import React, { useMemo, useState } from 'react';
import { ArrowUpRightIcon, BarChart3Icon, BoxesIcon, ChevronDownIcon, CircleDollarSignIcon, PackageCheckIcon, TrendingUpIcon } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CategoryPerformance } from './CategoryPerformance';
import { SalesBySku } from './SalesBySku';
import { NisoloProductSales } from './NisoloProductSales';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
const periods = ['Today', 'Yesterday', 'Month to date', 'Quarter to date', 'Year to date', 'All time'] as const;
type Period = (typeof periods)[number];
type ChartPoint = {
  label: string;
  sales: number;
  units: number;
};
type PeriodPerformance = {
  data: ChartPoint[];
  sales: number;
  units: number;
  averageUnitValue: string;
  sellThrough: string;
  comparison: string;
};
const performanceByPeriod: Record<Period, ChartPoint[]> = {
  Today: [{
    label: '9 AM',
    sales: 180,
    units: 6
  }, {
    label: '11 AM',
    sales: 360,
    units: 12
  }, {
    label: '1 PM',
    sales: 520,
    units: 17
  }, {
    label: '3 PM',
    sales: 760,
    units: 24
  }, {
    label: '5 PM',
    sales: 980,
    units: 31
  }],
  Yesterday: [{
    label: '9 AM',
    sales: 220,
    units: 8
  }, {
    label: '11 AM',
    sales: 390,
    units: 13
  }, {
    label: '1 PM',
    sales: 610,
    units: 20
  }, {
    label: '3 PM',
    sales: 840,
    units: 27
  }, {
    label: '5 PM',
    sales: 1120,
    units: 36
  }],
  'Month to date': [{
    label: 'May 1',
    sales: 820,
    units: 28
  }, {
    label: 'May 5',
    sales: 1280,
    units: 43
  }, {
    label: 'May 10',
    sales: 1840,
    units: 61
  }, {
    label: 'May 15',
    sales: 2390,
    units: 78
  }, {
    label: 'May 20',
    sales: 2970,
    units: 97
  }, {
    label: 'Today',
    sales: 3560,
    units: 116
  }],
  'Quarter to date': [{
    label: 'Apr',
    sales: 4380,
    units: 152
  }, {
    label: 'Late Apr',
    sales: 5820,
    units: 201
  }, {
    label: 'Early May',
    sales: 6840,
    units: 236
  }, {
    label: 'Mid May',
    sales: 7920,
    units: 276
  }, {
    label: 'Today',
    sales: 9410,
    units: 322
  }],
  'Year to date': [{
    label: 'Jan',
    sales: 3840,
    units: 132
  }, {
    label: 'Feb',
    sales: 5270,
    units: 182
  }, {
    label: 'Mar',
    sales: 7340,
    units: 254
  }, {
    label: 'Apr',
    sales: 8920,
    units: 309
  }, {
    label: 'May',
    sales: 11120,
    units: 386
  }, {
    label: 'Jun',
    sales: 13840,
    units: 481
  }],
  'All time': [{
    label: 'Jul',
    sales: 5210,
    units: 181
  }, {
    label: 'Aug',
    sales: 7460,
    units: 256
  }, {
    label: 'Sep',
    sales: 6280,
    units: 217
  }, {
    label: 'Oct',
    sales: 9560,
    units: 331
  }, {
    label: 'Nov',
    sales: 11980,
    units: 415
  }, {
    label: 'Dec',
    sales: 14680,
    units: 508
  }]
};
const eddyPerformanceByPeriod: Record<Period, PeriodPerformance> = {
  Today: {
    data: [{
      label: 'Today',
      sales: 0,
      units: 0
    }],
    sales: 0,
    units: 0,
    averageUnitValue: '$0.00',
    sellThrough: '0%',
    comparison: 'No sales recorded today'
  },
  Yesterday: {
    data: [{
      label: '12 AM',
      sales: 0,
      units: 0
    }, {
      label: '6 AM',
      sales: 0,
      units: 0
    }, {
      label: '12 PM',
      sales: 0,
      units: 0
    }, {
      label: '6 PM',
      sales: 0,
      units: 0
    }, {
      label: '12 AM',
      sales: 0,
      units: 0
    }],
    sales: 0,
    units: 0,
    averageUnitValue: '$0.00',
    sellThrough: '0%',
    comparison: 'No sales recorded yesterday'
  },
  'Month to date': {
    data: [{
      label: 'Jun 4',
      sales: 520,
      units: 4
    }, {
      label: 'Jun 7',
      sales: 380,
      units: 3
    }, {
      label: 'Jun 10',
      sales: 720,
      units: 6
    }, {
      label: 'Jun 14',
      sales: 180,
      units: 2
    }, {
      label: 'Jun 18',
      sales: 430,
      units: 4
    }, {
      label: 'Jun 22',
      sales: 150,
      units: 1
    }, {
      label: 'Jun 26',
      sales: 850,
      units: 7
    }, {
      label: 'Jun 30',
      sales: 1410,
      units: 11
    }, {
      label: 'Jul 3',
      sales: 160,
      units: 1
    }, {
      label: 'Jul 7',
      sales: 850,
      units: 7
    }, {
      label: 'Jul 10',
      sales: 920,
      units: 8
    }, {
      label: 'Jul 14',
      sales: 400,
      units: 3
    }, {
      label: 'Jul 17',
      sales: 780,
      units: 6
    }, {
      label: 'Jul 21',
      sales: 180,
      units: 2
    }, {
      label: 'Jul 26',
      sales: 300,
      units: 3
    }],
    sales: 8609,
    units: 68,
    averageUnitValue: '$126.60',
    sellThrough: '8%',
    comparison: 'Since last month'
  },
  'Quarter to date': {
    data: [{
      label: 'May',
      sales: 10870,
      units: 148
    }, {
      label: 'Jun',
      sales: 34210,
      units: 266
    }, {
      label: 'Jul',
      sales: 8609,
      units: 68
    }],
    sales: 53689,
    units: 482,
    averageUnitValue: '$111.39',
    sellThrough: '29%',
    comparison: 'Since last quarter'
  },
  'Year to date': {
    data: [{
      label: 'Dec',
      sales: 3200,
      units: 30
    }, {
      label: 'Jan',
      sales: 6800,
      units: 72
    }, {
      label: 'Feb',
      sales: 17700,
      units: 168
    }, {
      label: 'Mar',
      sales: 6500,
      units: 58
    }, {
      label: 'Apr',
      sales: 33000,
      units: 340
    }, {
      label: 'May',
      sales: 21900,
      units: 224
    }, {
      label: 'Jun',
      sales: 27800,
      units: 255
    }, {
      label: 'Jul',
      sales: 22400,
      units: 68
    }, {
      label: 'Aug',
      sales: 7500,
      units: 55
    }, {
      label: 'Sep',
      sales: 6100,
      units: 48
    }, {
      label: 'Oct',
      sales: 5800,
      units: 42
    }, {
      label: 'Nov',
      sales: 4800,
      units: 36
    }, {
      label: 'Dec',
      sales: 44000,
      units: 365
    }, {
      label: 'Jan',
      sales: 14000,
      units: 110
    }, {
      label: 'Feb',
      sales: 22500,
      units: 174
    }, {
      label: 'Mar',
      sales: 10800,
      units: 88
    }, {
      label: 'Apr',
      sales: 7300,
      units: 64
    }, {
      label: 'May',
      sales: 10600,
      units: 88
    }, {
      label: 'Jun',
      sales: 8609,
      units: 68
    }],
    sales: 118763,
    units: 1215,
    averageUnitValue: '$97.75',
    sellThrough: '46%',
    comparison: 'Since last year'
  },
  'All time': {
    data: [{
      label: 'Jun 2025',
      sales: 12000,
      units: 171
    }, {
      label: 'Jul 2025',
      sales: 22800,
      units: 287
    }, {
      label: 'Aug 2025',
      sales: 7600,
      units: 92
    }, {
      label: 'Sep 2025',
      sales: 5900,
      units: 70
    }, {
      label: 'Oct 2025',
      sales: 5400,
      units: 66
    }, {
      label: 'Nov 2025',
      sales: 6000,
      units: 77
    }, {
      label: 'Dec 2025',
      sales: 43800,
      units: 550
    }, {
      label: 'Jan 2026',
      sales: 14000,
      units: 184
    }, {
      label: 'Feb 2026',
      sales: 22500,
      units: 301
    }, {
      label: 'Mar 2026',
      sales: 10800,
      units: 143
    }, {
      label: 'Apr 2026',
      sales: 7300,
      units: 96
    }, {
      label: 'May 2026',
      sales: 10800,
      units: 142
    }, {
      label: 'Jun 2026',
      sales: 8000,
      units: 105
    }],
    sales: 325875,
    units: 4758,
    averageUnitValue: '$68.49',
    sellThrough: '74%',
    comparison: 'All time'
  }
};
const conditionRows = [{
  label: 'Rescue',
  units: '3,550',
  sales: '$276,357',
  aur: '$78',
  discount: '76%',
  className: 'bg-[#4F8A5B]'
}, {
  label: 'Damaged',
  units: '1,199',
  sales: '$48,547',
  aur: '$40',
  discount: '0%',
  className: 'bg-[#C75A47]'
}, {
  label: 'Resale',
  units: '9',
  sales: '$971',
  aur: '$108',
  discount: '0%',
  className: 'bg-[#4D9CC7]'
}];
type ChannelPerformance = {
  name: string;
  orders: string;
  units: string;
  sales: string;
  averageOrder: string;
  aur: string;
  share: string;
  icon: 'manymoons' | 'whatnot' | 'shopify' | 'poshmark' | 'prive';
};
const eddyChannelPerformance: ChannelPerformance[] = [{
  name: 'Manymoons',
  orders: '1,205',
  units: '1,647',
  sales: '$172,228',
  averageOrder: '$143',
  aur: '$105',
  share: '52.9%',
  icon: 'manymoons'
}, {
  name: 'Whatnot',
  orders: '1,201',
  units: '2,789',
  sales: '$113,718',
  averageOrder: '$95',
  aur: '$41',
  share: '34.9%',
  icon: 'whatnot'
}, {
  name: 'Collective',
  orders: '181',
  units: '227',
  sales: '$29,563',
  averageOrder: '$163',
  aur: '$130',
  share: '9.1%',
  icon: 'shopify'
}, {
  name: 'Circular Shop',
  orders: '36',
  units: '47',
  sales: '$7,327',
  averageOrder: '$204',
  aur: '$156',
  share: '2.2%',
  icon: 'manymoons'
}, {
  name: 'Poshmark',
  orders: '15',
  units: '17',
  sales: '$1,920',
  averageOrder: '$128',
  aur: '$113',
  share: '0.6%',
  icon: 'poshmark'
}, {
  name: 'Prive',
  orders: '4',
  units: '31',
  sales: '$1,118',
  averageOrder: '$280',
  aur: '$36',
  share: '0.3%',
  icon: 'prive'
}];
const channelIconFor = (name: string): ChannelPerformance['icon'] => {
  const normalized = name.toLowerCase();
  if (normalized.includes('whatnot')) return 'whatnot';
  if (normalized.includes('poshmark')) return 'poshmark';
  if (normalized.includes('prive') || normalized.includes('privé')) return 'prive';
  if (normalized.includes('shopify') || normalized.includes('collective')) return 'shopify';
  return 'manymoons';
};
export function PerformanceDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const [period, setPeriod] = useState<Period>('Year to date');
  const [periodOpen, setPeriodOpen] = useState(false);
  const isNisolo = selectedOwner === 'Nisolo';
  const {
    data: orders,
    loading: ordersLoading,
    error: ordersError
  } = useNisoloOrders(isNisolo);
  const nisoloPerformance = useMemo(() => {
    if (!orders) return null;
    const points = period === 'Today' || period === 'Yesterday' || period === 'Month to date' ? orders.daily.slice(-30) : orders.monthly;
    const scoped = points.length > 0 ? points : orders.monthly;
    return {
      data: scoped.map(point => ({
        label: point.label,
        sales: point.sales,
        units: point.units
      })),
      sales: orders.sales,
      units: orders.units,
      averageUnitValue: orders.aur.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2
      }),
      sellThrough: orders.msrpValue > 0 ? `${Math.round(orders.sales / orders.msrpValue * 100)}%` : '0%',
      comparison: `${orders.firstOrder} – ${orders.lastOrder}`
    };
  }, [orders, period]);
  const eddyPerformance = selectedOwner === 'Eddy' ? eddyPerformanceByPeriod[period] : null;
  const activePerformance = nisoloPerformance ?? eddyPerformance;
  const data = activePerformance?.data ?? performanceByPeriod[period];
  const fallbackTotals = useMemo(() => data[data.length - 1] ?? {
    sales: 0,
    units: 0
  }, [data]);
  const totals = activePerformance ?? {
    sales: fallbackTotals.sales,
    units: fallbackTotals.units,
    comparison: `${period} performance`
  };
  const channelPerformance: ChannelPerformance[] = isNisolo && orders ? orders.channels.map(channel => ({
    name: channel.label,
    orders: channel.orders.toLocaleString('en-US'),
    units: channel.units.toLocaleString('en-US'),
    sales: formatCurrency(channel.sales),
    averageOrder: formatCurrency(channel.orders > 0 ? channel.sales / channel.orders : 0),
    aur: formatCurrency(channel.units > 0 ? channel.sales / channel.units : 0),
    share: `${orders.sales > 0 ? (channel.sales / orders.sales * 100).toFixed(1) : '0.0'}%`,
    icon: channelIconFor(channel.label)
  })) : selectedOwner === 'Eddy' ? eddyChannelPerformance : [];
  const conditionPerformance = isNisolo && orders ? orders.conditions.map((condition, index) => ({
    label: condition.label,
    units: condition.units.toLocaleString('en-US'),
    sales: formatCurrency(condition.sales),
    aur: formatCurrency(condition.units > 0 ? condition.sales / condition.units : 0),
    discount: `${condition.sales + condition.discount > 0 ? Math.round(condition.discount / (condition.sales + condition.discount) * 100) : 0}%`,
    className: ['bg-[#4F8A5B]', 'bg-[#C75A47]', 'bg-[#4D9CC7]', 'bg-[#C9A04E]'][index % 4]
  })) : conditionRows;
  const summaryMetrics = activePerformance ? {
    averageUnitValue: activePerformance.averageUnitValue,
    sellThrough: activePerformance.sellThrough
  } : {
    averageUnitValue: '$0.00',
    sellThrough: '0%'
  };
  const summaryDetail = isNisolo && orders ? 'All recorded orders' : `For ${period.toLowerCase()}`;
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="performance-heading" data-id="element-1278" data-mp="3lqbpsjz32nmk">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1279" data-mp="3gu3gvobhbqnu">
        <div data-id="element-1280" data-mp="37276bds7i82q">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1281" data-mp="372h07cgdgzdp">
            <BarChart3Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1282" data-mp="8d8nczgknimf" />
            Performance
          </span>
          <h1 id="performance-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1283" data-mp="3kozn9o6773w">
            Recovery performance
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1284" data-mp="22sxb9uemo90l">
            {selectedOwner ? `A high-level view of ${selectedOwner}'s recovery performance across approved channels.` : 'Select an owner in the sidebar to focus this view on one recovery program.'}
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1285" data-mp="1f2l2yifh0a9h">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1286" data-mp="1j5rb5vm58bil" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <section className="mt-8 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-label="Sales performance chart" data-id="element-1287" data-mp="1a3hz31b88a33">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between" data-id="element-1288" data-mp="2zjppemi03qzq">
          <div data-id="element-1289" data-mp="34gpy7568j7uj">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-1290" data-mp="34h95lry9edub">Financial performance</p>
            <div className="mt-2 flex flex-wrap items-end gap-x-8 gap-y-4" data-id="element-1291" data-mp="sup6u2f0chj6">
              <div data-id="element-1292" data-mp="nwnnrhhl0i7p">
                <p className="font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl" data-id="element-1293" data-mp="nx5tr5u1qast">{formatCurrency(totals.sales)}</p>
                <p className="mt-1 text-sm text-ink/55" data-id="element-1294" data-mp="xtscnp2tars7">Total sales</p>
              </div>
              <div className="border-l border-ink/10 pl-5" data-id="element-1295" data-mp="1bkx443w10drg">
                <p className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-1296" data-mp="1bjc8eaxz6os0">{totals.units.toLocaleString('en-US')}</p>
                <p className="mt-1 text-sm text-ink/55" data-id="element-1297" data-mp="1lj37yr1e7tyy">Total units sold</p>
              </div>
              <span className="inline-flex items-center gap-1 pb-1 text-sm font-semibold text-forest" data-id="element-1298" data-mp="1gk09xqjtyo0h">
                <TrendingUpIcon className="h-4 w-4" data-id="element-1299" data-mp="3na6h1bvmx0he" />
                {totals.comparison}
              </span>
            </div>
            <p className="mt-2 text-sm text-ink/55" data-id="element-1300" data-mp="3ic4vmkaqkp2r">Gross recovery value · {period.toLowerCase()}</p>
          </div>
          <div className="relative w-full sm:w-48" data-id="element-1301" data-mp="29dn13lvrxug2">
            <button type="button" onClick={() => setPeriodOpen(isOpen => !isOpen)} aria-expanded={periodOpen} aria-controls="performance-periods" className="flex w-full items-center justify-between rounded-xl border border-ink/10 bg-cream px-3 py-2.5 text-left text-sm font-semibold text-ink transition-colors hover:border-[#BB5E40]/35 focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/15" data-id="element-1302" data-mp="2edj7frry1xys">
              {period}
              <ChevronDownIcon className={`h-4 w-4 text-ink/45 transition-transform ${periodOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1303" data-mp="1zovdfpxqxway" />
            </button>
            {periodOpen && <div id="performance-periods" role="listbox" aria-label="Performance period" className="absolute right-0 z-10 mt-2 w-full overflow-hidden rounded-xl border border-ink/10 bg-paper p-1.5 shadow-lg" data-id="element-1304" data-mp="17xffx0unip5y">
                {periods.map(item => <button key={item} type="button" role="option" aria-selected={period === item} onClick={() => {
              setPeriod(item);
              setPeriodOpen(false);
            }} className={`flex w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${period === item ? 'bg-[#BB5E40] text-cream' : 'text-ink/70 hover:bg-cream hover:text-ink'}`} data-id="element-1305" data-mp="14ezvm4bbqbbd">
                    {item}
                  </button>)}
              </div>}
          </div>
        </div>

        <div className="mt-7 h-64 sm:h-80" data-id="element-1306" data-mp="7n63ayvn1qqa">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1307" data-mp="2qfla609wl28">
            <AreaChart data={data} margin={{
            top: 10,
            right: 8,
            left: -15,
            bottom: 0
          }} data-id="element-1308" data-mp="2s0i62a28g39">
              <defs data-id="element-1309" data-mp="3tyk001ebd6b2">
                <linearGradient id="recovery-area" x1="0" y1="0" x2="0" y2="1" data-id="element-1310" data-mp="3k8uz5pel0tlr">
                  <stop offset="0%" stopColor="#BB5E40" stopOpacity={0.34} data-id="element-1311" data-mp="3kaxver6bviyx" />
                  <stop offset="95%" stopColor="#BB5E40" stopOpacity={0.02} data-id="element-1312" data-mp="3p9iycycysgr2" />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#1C1A17" strokeOpacity={0.1} strokeDasharray="3 5" data-id="element-1313" data-mp="3ad1lu3t1gw2x" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} dy={10} data-id="element-1314" data-mp="6tykma2ouc68" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} tickFormatter={(value: number) => formatAxisCurrency(value)} data-id="element-1315" data-mp="btl2wxxdiilw" />
              <Tooltip content={<ChartTooltip data-id="element-1317" data-mp="238h72dtoje6" />} cursor={{
              stroke: '#BB5E40',
              strokeOpacity: 0.25,
              strokeWidth: 1
            }} data-id="element-1316" data-mp="3t7gmdxv00wsv" />
              <Area type="monotone" dataKey="sales" stroke="#BB5E40" strokeWidth={3} fill="url(#recovery-area)" activeDot={{
              r: 5,
              fill: '#BB5E40',
              stroke: '#F7F4EC',
              strokeWidth: 3
            }} data-id="element-1318" data-mp="pt2u1jy567yf" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]" data-id="element-1319" data-mp="2f7vd9z40v864">
        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="condition-heading" data-id="element-1320" data-mp="2aalh80m4ny9b">
          <div className="flex items-center gap-2" data-id="element-1321" data-mp="2k0oi0mzhqgi7">
            <PackageCheckIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1322" data-mp="2k1zkmjh79i7q" />
            <h2 id="condition-heading" className="text-base font-semibold" data-id="element-1323" data-mp="1wc58k44pfxoj">Sales by condition</h2>
          </div>
          <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-1324" data-mp="1rdaqj4ekad5h">
            <table className="w-full min-w-[470px] text-left text-xs" data-id="element-1325" data-mp="25ca5tlg6pfoz">
              <thead className="border-b border-ink/10 text-ink/50" data-id="element-1326" data-mp="259fmjx8wq0x5">
                <tr data-id="element-1327" data-mp="218lz14zdmo4s">
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1328" data-mp="21c9tq7gk5gl3">Condition</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1329" data-mp="3gpcmshm7479o">Units sold</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1330" data-mp="3cokf3ssz2afi">Total sales</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1331" data-mp="1a3zu8mpfv233">AUR</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1332" data-mp="1f0yqbasnxj3x">Avg discount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-1333" data-mp="1jzjo73kfzqp7">
                {conditionPerformance.map(condition => <tr key={condition.label} data-id="element-1334" data-mp="1ote6c2kefxm6">
                    <th scope="row" className="py-3 text-left font-medium" data-id="element-1335" data-mp="1sty4n9gpl79w">
                      <span className="inline-flex items-center gap-2" data-id="element-1336" data-mp="1xrzldnqnwui7">
                        <span className={`h-2.5 w-2.5 rounded-full ${condition.className}`} data-id="element-1337" data-mp="1xu2kqydpfzzl" />
                        {condition.label}
                      </span>
                    </th>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1338" data-mp="27ocotdc0emq5">{condition.units}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1339" data-mp="8fe8cx546i1k">{condition.sales}</td>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1340" data-mp="de91ot0pjozx">{condition.aur}</td>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1341" data-mp="2qos7vg1tvz1y">{condition.discount}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </article>

        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="channel-heading" data-id="element-1342" data-mp="2cv9fwcv608nr">
          <div className="flex items-center gap-2" data-id="element-1343" data-mp="39lthje6bunc8">
            <CircleDollarSignIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1344" data-mp="39jfmv6fk7d57" />
            <h2 id="channel-heading" className="text-base font-semibold" data-id="element-1345" data-mp="35kq04n8msj8w">Channel performance</h2>
          </div>
          <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-1346" data-mp="30mokkcaez226">
            {channelPerformance.length > 0 ? <table className="w-full min-w-[700px] text-left text-xs" data-id="element-1347" data-mp="1p5otfjzsyt9q">
                <thead className="border-b border-ink/10 text-ink/50" data-id="element-1348" data-mp="1p6zxzm12o51w">
                  <tr data-id="element-1349" data-mp="1k5kevzigg8m5">
                    <th scope="col" className="pb-2 font-semibold" data-id="element-1350" data-mp="1k5ale1y7bjbp">Channel</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1351" data-mp="1vuihxrcjw7p4">Orders</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1352" data-mp="1r1z4qvhrzeku">Units</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1353" data-mp="1m34ej8hhanqp">Total sales</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1354" data-mp="1h5cji5ljelia">Avg order</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1355" data-mp="2et4kzbrmo8vo">AUR</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1356" data-mp="29ti50ukf0ej6">% of total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink/10" data-id="element-1357" data-mp="24tdrndeszzx6">
                  {channelPerformance.map(channel => <tr key={channel.name} data-id="element-1358" data-mp="1zusmczkp2q1v">
                      <th scope="row" className="py-3 text-left font-medium text-ink" data-id="element-1359" data-mp="tf06whfck0ld">
                        <span className="flex items-center gap-2.5" data-id="element-1360" data-mp="og5c0h43ulkw">
                          <ChannelMark type={channel.icon} data-id="element-1361" data-mp="oeu88hantdts" />
                          {channel.name}
                        </span>
                      </th>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1362" data-mp="351txm037ee4u">{channel.orders}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1363" data-mp="3a1ev062q6efg">{channel.units}</td>
                      <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1364" data-mp="3esojh3cjfbpr">{channel.sales}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1365" data-mp="3jqgc5zl0b1v0">{channel.averageOrder}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1366" data-mp="3nrs44brcrz6d">{channel.aur}</td>
                      <td className="py-3 text-right font-semibold tabular-nums text-[#BB5E40]" data-id="element-1367" data-mp="3st7mtx625tll">{channel.share}</td>
                    </tr>)}
                </tbody>
              </table> : <div className="py-8 text-center text-sm text-ink/55" data-id="element-1368" data-mp="1yfzqu26cjbyd">Select an owner to view channel performance.</div>}
          </div>
        </article>
      </section>

      <section className="mt-5 grid gap-5 sm:grid-cols-3" aria-label="Performance summary" data-id="element-1369" data-mp="1vdboybbmqfcw">
        <SummaryCard icon={BoxesIcon} label="Units sold" value={totals.units.toLocaleString('en-US')} detail={summaryDetail} data-id="element-1370" data-mp="1rc08avaqh4gu" />
        <SummaryCard icon={CircleDollarSignIcon} label="Average unit value" value={summaryMetrics.averageUnitValue} detail={summaryDetail} data-id="element-1371" data-mp="25c1b4gak6lyy" />
        <SummaryCard icon={ArrowUpRightIcon} label={isNisolo ? 'Recovery vs MSRP' : 'Sell-through'} value={summaryMetrics.sellThrough} detail={summaryDetail} data-id="element-1372" data-mp="2eb683i6h2790" />
      </section>

      {isNisolo ? <>
          {ordersLoading && <p className="mt-5 rounded-2xl border border-ink/10 bg-paper px-5 py-10 text-center text-sm text-ink/55" data-id="element-1373" data-mp="2o0pqynu6q13v">Loading Nisolo order history…</p>}
          {ordersError && !ordersLoading && <p className="mt-5 rounded-2xl border border-ink/10 bg-paper px-5 py-10 text-center text-sm text-ink/55" data-id="element-1374" data-mp="2xznbzgsejci4">{ordersError}</p>}
          {orders && <NisoloProductSales products={orders.products} totalUnits={orders.units} data-id="element-1375" data-mp="3clkrz66bziel" />}
        </> : <>
          <CategoryPerformance selectedOwner={selectedOwner} data-id="element-1376" data-mp="3gmo179tvig6u" />
          <SalesBySku selectedOwner={selectedOwner} data-id="element-1377" data-mp="32o6k3uyh41xw" />
        </>}
    </div>;
}
function ChannelMark({
  type
}: {
  type: ChannelPerformance['icon'];
}) {
  const iconSources = {
    manymoons: 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg',
    whatnot: 'https://cdn.magicpatterns.com/uploads/tKhY3ZxiXEuMX34dV7VA57/whatnot.svg',
    poshmark: 'https://cdn.magicpatterns.com/uploads/n5jkPZQbazKpxu5cMnVHR4/poshmark.svg',
    prive: 'https://cdn.magicpatterns.com/uploads/fHmVmW1X2zeRckQHVqfLBY/prive.svg'
  } as const;
  if (type === 'shopify') {
    return <span aria-label="Shopify" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40]" data-id="element-1378" data-mp="29ziuxoalssf2">
        <span aria-hidden="true" className="h-4 w-4 bg-cream" style={{
        WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/7LeZM2476qhffRRTPqij7m/shopify_glyph_black.svg)',
        maskImage: 'url(https://cdn.magicpatterns.com/uploads/7LeZM2476qhffRRTPqij7m/shopify_glyph_black.svg)',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain'
      }} data-id="element-1379" data-mp="2eylqmi4p1mad" />
      </span>;
  }
  return <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40] p-1.5" data-id="element-1380" data-mp="2ui3uia3vigo0">
      <img src={iconSources[type]} alt="" className="h-full w-full object-contain" data-id="element-1381" data-mp="2pl3yks0qmuam" />
    </span>;
}
function SummaryCard({
  icon: Icon,
  label,
  value,
  detail
}: {
  icon: typeof BoxesIcon;
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-1382" data-mp="itmbm8i7rjm8">
      <Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1383" data-mp="qdn9kslwgfs2" />
      <p className="mt-4 text-sm text-ink/55" data-id="element-1384" data-mp="qddgux9gzuio">{label}</p>
      <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1385" data-mp="vc8cx0wg7fkq">{value}</p>
      <p className="mt-1 text-xs text-ink/50" data-id="element-1386" data-mp="102x3me0b08it">{detail}</p>
    </article>;
}
function ChartTooltip({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: Array<{
    value?: number;
  }>;
  label?: string;
}) {
  if (!active || !payload?.[0]?.value) return null;
  return <div className="rounded-xl border border-ink/10 bg-paper px-3 py-2 shadow-lg" data-id="element-1387" data-mp="cdw1t9lz7vmi">
      <p className="text-xs text-ink/50" data-id="element-1388" data-mp="cfp7omormbtg">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-ink" data-id="element-1389" data-mp="hbxgzjga7asp">{formatCurrency(payload[0].value)}</p>
    </div>;
}
function formatCurrency(value: number) {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });
}
function formatAxisCurrency(value: number) {
  if (value >= 1000) return `$${Math.round(value / 1000)}k`;
  return `$${value}`;
}